import {useState} from 'react'
import {Breadcrumb, Button, Card, Nav, Spinner} from 'react-bootstrap'
import {useNavigate, useParams} from 'react-router-dom'
import {useQuery} from '@apollo/client'
import {GET_ORGANIZATION_BY_ID} from '../../gql/queries/organizationsQuery'
import {prependBackendUrl} from '../../gql/client'
import {parseId} from '../../helpers'
import {useGetUser} from '../../store/userStore'
import {useOrganizationForm} from './hooks/useOrganizationForm'
import {OrganizationGeneralForm} from './components/OrganizationGeneralForm'
import {OrganizationSettingsTab} from './components/OrganizationSettingsTab'
import {OrganizationChannelConfigPanel} from '../channelConfig/components/OrganizationChannelConfigPanel'
import {OrganizationChannelsTable} from '../channelConfig/components/OrganizationChannelsTable'
import {OrganizationEmailSettingsTab} from './components/OrganizationEmailSettingsTab'
import {OrganizationEmailSetupTab} from './components/OrganizationEmailSetupTab'
import {OrganizationOnboardingTab} from './components/OrganizationOnboardingTab'

type OrgTab =
  | 'general'
  | 'settings'
  | 'channelsConfig'
  | 'channelsList'
  | 'otherSettings'
  | 'emailConfigurations'
  | 'onboarding'

type EmailSubTab = 'emailSetup' | 'emailSettings'

export const OneOrganizationPage = () => {
  const {id: rawId} = useParams()
  // Accepts either a raw pk or a relay-encoded id (e.g. a manually pasted/bookmarked
  // URL, or an older link) — parseId() passes numeric strings through untouched.
  const id = rawId ? `${parseId(rawId)}` : undefined
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<OrgTab>('general')
  const [activeEmailSubTab, setActiveEmailSubTab] = useState<EmailSubTab>('emailSetup')
  const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const isAdmin = userGroups.includes('Administrador') || isSuperAdmin

  const {data, loading, refetch} = useQuery(GET_ORGANIZATION_BY_ID, {
    variables: {id},
    skip: !id,
    fetchPolicy: 'cache-and-network',
  })
  const org = data?.organizationById

  const {
    form,
    handleChange,
    handleSave,
    saving,
    logoPreview,
    setLogoPreview,
    selectedLogo,
    setSelectedLogo,
    logoLoadError,
    setLogoLoadError,
    logoInputRef,
    handleLogoSelect,
    handleLogoUpload,
    uploadingLogo,
    tplLogoPreview,
    setTplLogoPreview,
    selectedTplLogo,
    setSelectedTplLogo,
    tplLogoLoadError,
    setTplLogoLoadError,
    tplLogoInputRef,
    handleTplLogoSelect,
    handleTplLogoUpload,
    uploadingTplLogo,
  } = useOrganizationForm(org, () => refetch())

  if (loading && !org) {
    return (
      <div className='d-flex justify-content-center py-10'>
        <Spinner animation='border' variant='primary' />
      </div>
    )
  }

  if (!org) {
    return (
      <div className='container-xxl py-5'>
        <p className='text-muted'>Organization not found, or you don't have access to it.</p>
      </div>
    )
  }

  return (
    <div className='container-xxl py-5'>
      <header className='header mb-3'>
        <Breadcrumb>
          <Breadcrumb.Item onClick={() => navigate('/organizations')}>
            Organizations
          </Breadcrumb.Item>
          <Breadcrumb.Item active>{org.name}</Breadcrumb.Item>
        </Breadcrumb>
      </header>

      <div className='d-flex align-items-center gap-4 mb-6'>
        {org.logo ? (
          <img
            src={prependBackendUrl(org.logo)}
            alt={org.name}
            style={{width: 56, height: 56, objectFit: 'contain', borderRadius: 8}}
          />
        ) : (
          <div
            className='bg-primary bg-opacity-10 text-primary fw-bold d-flex align-items-center justify-content-center rounded'
            style={{width: 56, height: 56, fontSize: 20}}
          >
            {org.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
        <div>
          <div className='fw-semibold fs-2 text-gray-900'>{org.name}</div>
          <div className='text-muted fs-7'>{org.owner}</div>
          <div className='text-muted fs-7'>{org.email}</div>
        </div>
      </div>

      <Card className='mb-0'>
        <Card.Header className='border-0 pt-5 px-5 pb-0'>
          <Nav
            variant='tabs'
            activeKey={activeTab}
            onSelect={(k) => setActiveTab((k as OrgTab) || 'general')}
          >
            <Nav.Item>
              <Nav.Link eventKey='general' className='fw-bold'>
                <i className='bi bi-building me-2' />
                General
              </Nav.Link>
            </Nav.Item>
            {isAdmin && (
              <Nav.Item>
                <Nav.Link eventKey='settings' className='fw-bold'>
                  <i className='bi bi-sliders me-2' />
                  Environment Setup
                </Nav.Link>
              </Nav.Item>
            )}
            {isAdmin && (
              <Nav.Item>
                <Nav.Link eventKey='channelsConfig' className='fw-bold'>
                  <i className='bi bi-broadcast me-2' />
                  Channels Config
                </Nav.Link>
              </Nav.Item>
            )}
            {isAdmin && (
              <Nav.Item>
                <Nav.Link eventKey='channelsList' className='fw-bold'>
                  <i className='bi bi-table me-2' />
                  Organization Channels
                </Nav.Link>
              </Nav.Item>
            )}
            {isAdmin && (
              <Nav.Item>
                <Nav.Link eventKey='otherSettings' className='fw-bold'>
                  <i className='bi bi-gear-wide-connected me-2' />
                  Other Settings
                </Nav.Link>
              </Nav.Item>
            )}
            {isAdmin && (
              <Nav.Item>
                <Nav.Link eventKey='emailConfigurations' className='fw-bold'>
                  <i className='bi bi-envelope me-2' />
                  Email Configurations
                </Nav.Link>
              </Nav.Item>
            )}
            {isAdmin && (
              <Nav.Item>
                <Nav.Link eventKey='onboarding' className='fw-bold'>
                  <i className='bi bi-person-check me-2' />
                  Onboarding
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
        </Card.Header>

        <Card.Body className='px-5 pb-5 pt-3'>
          {activeTab === 'general' && (
            <>
              <OrganizationGeneralForm
                editOrg={org}
                form={form}
                handleChange={handleChange}
                logoPreview={logoPreview}
                setLogoPreview={setLogoPreview}
                selectedLogo={selectedLogo}
                setSelectedLogo={setSelectedLogo}
                logoLoadError={logoLoadError}
                setLogoLoadError={setLogoLoadError}
                logoInputRef={logoInputRef}
                handleLogoSelect={handleLogoSelect}
                handleLogoUpload={handleLogoUpload}
                uploadingLogo={uploadingLogo}
                tplLogoPreview={tplLogoPreview}
                setTplLogoPreview={setTplLogoPreview}
                selectedTplLogo={selectedTplLogo}
                setSelectedTplLogo={setSelectedTplLogo}
                tplLogoLoadError={tplLogoLoadError}
                setTplLogoLoadError={setTplLogoLoadError}
                tplLogoInputRef={tplLogoInputRef}
                handleTplLogoSelect={handleTplLogoSelect}
                handleTplLogoUpload={handleTplLogoUpload}
                uploadingTplLogo={uploadingTplLogo}
              />
              <div className='d-flex justify-content-end mt-5 pt-4 border-top'>
                <Button variant='primary' onClick={handleSave} disabled={saving} className='px-6'>
                  {saving ? (
                    <>
                      <Spinner animation='border' size='sm' className='me-2' />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </>
          )}

          {activeTab === 'settings' && isAdmin && (
            <div>
              <h5 className='fw-bold mb-3'>System Parameters</h5>
              <OrganizationSettingsTab organizationId={org.id} />
            </div>
          )}

          {activeTab === 'channelsConfig' && isAdmin && (
            <OrganizationChannelConfigPanel
              organizationId={String(parseId(org.id))}
              sections={{ telephony: true, messaging: true }}
            />
          )}

          {activeTab === 'channelsList' && isAdmin && (
            <OrganizationChannelsTable organizationId={String(parseId(org.id))} />
          )}

          {activeTab === 'otherSettings' && isAdmin && (
            <OrganizationChannelConfigPanel
              organizationId={String(parseId(org.id))}
              sections={{ aiAssistant: true, integrations: true }}
            />
          )}

          {activeTab === 'emailConfigurations' && isAdmin && (
            <>
              <Nav
                variant='pills'
                activeKey={activeEmailSubTab}
                onSelect={(k) => setActiveEmailSubTab((k as EmailSubTab) || 'emailSetup')}
                className='mb-5'
              >
                <Nav.Item>
                  <Nav.Link eventKey='emailSetup' className='fw-bold'>
                    <i className='bi bi-server me-2' />
                    Email Setup
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey='emailSettings' className='fw-bold'>
                    <i className='bi bi-envelope me-2' />
                    Email Settings
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              {activeEmailSubTab === 'emailSetup' && (
                <OrganizationEmailSetupTab organizationId={String(parseId(org.id))} />
              )}
              {activeEmailSubTab === 'emailSettings' && (
                <OrganizationEmailSettingsTab organizationId={String(parseId(org.id))} />
              )}
            </>
          )}

          {activeTab === 'onboarding' && isAdmin && (
            <OrganizationOnboardingTab organizationId={org.id} />
          )}
        </Card.Body>
      </Card>
    </div>
  )
}
